import {Component, inject, OnInit} from '@angular/core';
import {EntityPerformanceChartComponent, PerformanceRecord} from "./entity-performance-chart.component";
import {
    AggregatedDataPoint,
    DataType,
    MeasurementsResult,
    Organisation,
    PortfolioMeasurementsResult
} from "@flowmaps-com/flowmaps-typescriptmodels";
import {DashboardContext} from '../../dashboard/dashboard.context';
import {lodash} from "../../../common/utils";
import {map, mergeMap, Observable, of} from "rxjs";
import {RefdataUtils} from "../../refdata/refdata-utils";
import {ActiveElement, Chart, ChartEvent} from "chart.js";
import {Router} from "@angular/router";
import {AppContext} from "../../../app-context";
import {sendQuery} from "../../../common/app-common-utils";
import {Entity, EntityType} from "../../../handlers/entity";
import {HandleQuery} from "../../../common/handle-query";
import {Handler} from 'src/app/common/handler';
import {
    DataQueryFilters,
    MeasurementsHandlerComponent
} from "../../measurements-component/measurements-handler.component";
import {ChartDataPerMeasurement} from "../../../utils/chart-data-provider";
import {LocationRankingName} from "../../dashboard/dashboard.types";
import {
    loadLocationRankingNamePreference,
    locationRankingLabel,
    saveLocationRankingNamePreference
} from "./location-ranking-name";

@Component({
    selector: 'app-location-performance-chart',
    templateUrl: './entity-performance-chart.component.html',
    styleUrls: ['./entity-performance-chart.component.scss']
})
@Handler()
export class LocationPerformanceChartComponent extends EntityPerformanceChartComponent implements OnInit {
    private router: Router = inject(Router);
    private dashboardMeasurements: ChartDataPerMeasurement;
    selectedDataType: DataType = DataType.electricityConsumption;
    perSquareMeterEnabled = true;
    locationRankingNameOptions = Object.values(LocationRankingName);

    entityType = (): EntityType => EntityType.location

    get getSelectedDataType() {
        return this.dataType || super.getSelectedDataType;
    }

    ngOnInit() {
        super.ngOnInit();
        this.options.locationRankingName = this.showInReport ? LocationRankingName.Address
            : loadLocationRankingNamePreference(localStorage, AppContext.userProfile?.userId)
                || this.options.locationRankingName || LocationRankingName.Address;
        this.subscribeTo("getChartDataQueryFilters")
            .subscribe((f: DataQueryFilters) => {
                this.filters = f;
                this.startLoading();
            });
        this.subscribeTo("getDashboardMeasurements").subscribe({
            next: (data: ChartDataPerMeasurement) => {
                this.dashboardMeasurements = data;
                this.refresh();
            },
            error: this.showLoadError
        });
    }

    locationRankingNameChanged = (selectedName: LocationRankingName) => {
        this.options.locationRankingName = selectedName;
        saveLocationRankingNamePreference(localStorage, selectedName, AppContext.userProfile?.userId);
        this.refresh();
    }

    refresh = () => {
        this.startLoading();
        if (!this.dashboardMeasurements) return;
        if (this.dashboardMeasurements.entityType === EntityType.location) {
            this.setData(this.dashboardMeasurements);
        } else {
            this.showNoData();
        }
    }

    possibleDataTypes = (): DataType[] =>
        [DataType.electricityConsumption, DataType.electricityFeedIn, DataType.electricityConsumptionReactive,
            DataType.electricityGrossProduction, DataType.electricityIntermediateConsumption]
            .concat([DataType.gasConsumption, DataType.heatConsumption, DataType.coolingConsumption, DataType.waterConsumption, DataType.waterIntermediateConsumption, DataType.chargePointConsumption])
            .concat(AppContext.isAdmin() ? [DataType.gasConsumptionUncorrected] : []);

    createData = (result: ChartDataPerMeasurement): Observable<PerformanceRecord[]> => {
        return this.sendQuery("getByLocation", result).pipe(mergeMap((byLocation: MeasurementsResult[]) =>
            this.getEntities().pipe(map(entities => {
                const entityMap = new Map<string, Entity>();
                for (const entity of entities) {
                    entityMap.set(entity.location.locationId, entity);
                    for (const alias of entity.location.aliasIds || []) {
                        entityMap.set(alias, entity);
                    }
                }
                return Object.entries(lodash.groupBy(byLocation, d => d.entityId))
                    .map(([entityId, results]) => {
                        const entity = entityMap.get(entityId);
                        if (!entity) return null;
                        const area = RefdataUtils.getLocationArea(entity.location);
                        let value = 0;
                        let estimatedValue = 0;
                        for (const d of results) {
                            for (const m of this.measurementTypes()) {
                                for (const r of this.getMeasurementsForType(d.measurements, m) || []) value += r.value;
                                for (const r of this.getMeasurementsForType(d.estimatedMeasurements, m) || []) estimatedValue += r.value;
                            }
                        }
                        return <PerformanceRecord>{
                            entityId: entity.getEntityId(),
                            value: this.showPerSquareMeter() ? (area ? lodash.round(value / area, 1) : null) : value,
                            estimatedValue: this.showPerSquareMeter() ? (area ? lodash.round(estimatedValue / area, 1) : null) : estimatedValue,
                            label: locationRankingLabel(entity.location.info,
                                this.options.locationRankingName || LocationRankingName.Address),
                            childIds: entity.getChildIds()
                        }}).filter((e): e is PerformanceRecord => e !== null)}))));
    };

    private getEntities = (): Observable<Entity[]> => {
        return this.sendQuery("getChartDataQueryFilters").pipe(mergeMap((q: DataQueryFilters) => {
            const selectedEntities: Entity[] = q?.sources?.filter(t => t.type === this.entityType())
                .map(t => t.source) || [];
            return selectedEntities.length > 0 ? of(selectedEntities) : this.getAllEntities();
        }))
    }

    getAllEntities = (): Observable<Entity[]> => sendQuery("getLocationsAsEntities")

    navigateToEntity = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        const points = chart.getElementsAtEventForMode(event.native, 'nearest', {intersect: true}, true);

        if (points && elements.length > 0) {
            const clickedRecord: PerformanceRecord = this.currentPageRecords[points[0].index];
            if (clickedRecord.entityId) {
                this.sendCommandAndForget("selectEntity", clickedRecord.entityId);
            }
        } else if (this.organisations.length && this.filters.sources?.length) {
            this.sendCommandAndForget("clearSelection");
        }
    }

    onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        const target: any = event.native.target;
        target.style.cursor = elements.length > 0 ? "pointer" : "auto";
        if (elements.length > 0) {
            target.style.cursor = "pointer";
        } else if (this.organisations.length && this.filters.sources?.length) {
            target.style.cursor = "zoom-out";
        } else {
            target.style.cursor = "auto";
        }
    }

    private getMeasurementsForType(m: { [P in DataType]?: AggregatedDataPoint[] }, dataType: DataType): AggregatedDataPoint[] {
        const measurements = m[dataType] || [];
        if (dataType === DataType.heatConsumption) {
            return measurements.map(m => (<AggregatedDataPoint>{
                timeRange: m.timeRange,
                value: m.value * DashboardContext.gjToM3Rate
            }));
        }
        return measurements;
    }

    @HandleQuery("getByLocation")
    getByLocation(result: PortfolioMeasurementsResult): Observable<MeasurementsResult[]> {
        return this.sendQuery("getOrganisations")
            .pipe(map((o: Organisation[]) => MeasurementsHandlerComponent.mergeAliasesWithLocation(result, o)))
            .pipe(map(r =>
                (r || []).map(l => {
                    const measurements: { [P in DataType]?: AggregatedDataPoint[] } = {};
                    Object.entries(l.measurements)
                        .filter(e => this.measurementTypes().includes(e[0] as DataType))
                        .forEach(e => measurements[e[0]] = e[1]);
                    const estimatedMeasurements: { [P in DataType]?: AggregatedDataPoint[] } = {};
                    Object.entries(l.estimatedMeasurements)
                        .filter(e => this.measurementTypes().includes(e[0] as DataType))
                        .forEach(e => estimatedMeasurements[e[0]] = e[1]);
                    return <MeasurementsResult>{
                        entityId: l.entityId,
                        measurements: measurements,
                        estimatedMeasurements: estimatedMeasurements
                    };
                })));
    }
}
